import React from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '@/components/site/PageBanner';
import SEO from '@/components/site/SEO';
import {
  Linkedin, TrendingUp, BadgeCheck, Award, Users, Briefcase,
  MapPin, IndianRupee, Star, ArrowRight, ShieldCheck, Globe,
} from 'lucide-react';

const FOUNDERS = [
  {
    name: 'Saurabh Makharia',
    title: 'Founder & CEO',
    tagline: 'Angel Investor · AMFI Certified Mutual Fund Distributor',
    location: 'Thane, Maharashtra',
    linkedin: 'https://www.linkedin.com/in/saurabhmakh/',
    avatar: 'https://ui-avatars.com/api/?name=Saurabh+Makharia&background=0f3d78&color=fff&size=300&bold=true&rounded=false',
    accentColor: '#1e73be',
    accentBg: '#eaf3fb',
    bio: [
      'Saurabh Makharia is the driving force behind Finirio Technologies — a visionary entrepreneur with nearly a decade of experience in the financial services industry. His passion for making finance accessible to every Indian household led him to found Finirio, now one of Mumbai\'s fastest-growing loan and investment advisory platforms.',
      'An AMFI-certified Mutual Fund Distributor and a seasoned Angel Investor, Saurabh brings deep expertise across retail lending, wealth management, and business strategy. Under his leadership, Finirio has disbursed over ₹6,000 crores in loans and built a trusted network of 40+ banking partners.',
    ],
    credentials: [
      { icon: Award, text: 'AMFI Certified Mutual Fund Distributor (ARN-336808)' },
      { icon: TrendingUp, text: 'Angel Investor & Serial Entrepreneur' },
      { icon: Briefcase, text: 'Former VP Business Strategy — Broker Network (4B Networks)' },
      { icon: Star, text: '8+ Years in Financial Services & Lending' },
      { icon: Globe, text: 'Founder — Finirio Technologies Pvt. Ltd.' },
    ],
    stats: [
      { v: '₹6,000 Cr+', l: 'Loans Disbursed' },
      { v: '10,000+', l: 'Clients Served' },
      { v: '8+ Yrs', l: 'Industry Experience' },
    ],
  },
  {
    name: 'Smriti Makharia',
    title: 'Founder & Director',
    tagline: 'Investment Specialist · Business Growth Catalyst',
    location: 'Mumbai, Maharashtra',
    linkedin: 'https://in.linkedin.com/in/smriti-makharia-a025b4275',
    avatar: 'https://ui-avatars.com/api/?name=Smriti+Makharia&background=2ea690&color=fff&size=300&bold=true&rounded=false',
    accentColor: '#2ea690',
    accentBg: '#e7f6f2',
    bio: [
      'Smriti Makharia is the Co-Founder and Director of Finirio Technologies — a dynamic entrepreneur with a sharp eye for investments and an innate ability to build lasting client relationships. Her expertise in loan solutions and investment advisory has been instrumental in shaping Finirio\'s client-first philosophy.',
      'As a specialist in investments and business growth, Smriti oversees Finirio\'s portfolio advisory, partner ecosystem, and operational excellence. Her belief that every individual deserves transparent and intelligent financial guidance drives every product and service Finirio delivers.',
    ],
    credentials: [
      { icon: IndianRupee, text: 'Specialist in Investments & Loan Solutions' },
      { icon: TrendingUp, text: 'Business Growth Catalyst & Strategist' },
      { icon: Users, text: 'Director — Finirio Technologies Pvt. Ltd.' },
      { icon: ShieldCheck, text: 'Expert in Retail & MSME Lending' },
      { icon: Star, text: 'Entrepreneur · Mumbai Financial Ecosystem' },
    ],
    stats: [
      { v: '40+', l: 'Banking Partners' },
      { v: '500+', l: 'MF Investors' },
      { v: '100%', l: 'Transparent Advisory' },
    ],
  },
];

const FounderCard = ({ founder, reverse }) => (
  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[direction:rtl]' : ''}`}>
    {/* Photo side */}
    <div className={`${reverse ? 'lg:[direction:ltr]' : ''}`}>
      <div className="relative mx-auto max-w-[420px]">
        {/* Background accent shape */}
        <div
          className="absolute -bottom-4 -right-4 w-full h-full rounded-2xl"
          style={{ backgroundColor: founder.accentBg, zIndex: 0 }}
        />
        <div className="relative rounded-2xl overflow-hidden shadow-xl" style={{ zIndex: 1 }}>
          <img
            src={founder.avatar}
            alt={founder.name}
            className="w-full aspect-square object-cover"
            style={{ filter: 'brightness(1.05)' }}
          />
          {/* Name overlay */}
          <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/70 to-transparent">
            <p className="font-heading font-bold text-white text-[22px] leading-tight">{founder.name}</p>
            <p className="text-[13px] font-medium mt-0.5" style={{ color: founder.accentColor === '#1e73be' ? '#93c5fd' : '#6ee7b7' }}>
              {founder.title}
            </p>
          </div>
        </div>
        {/* Location badge */}
        <div className="absolute -top-3 -left-3 bg-white shadow-md border border-slate-100 rounded-full px-3 py-1.5 flex items-center gap-1.5 z-10">
          <MapPin className="w-3.5 h-3.5" style={{ color: founder.accentColor }} />
          <span className="text-[12px] font-medium text-slate-700">{founder.location}</span>
        </div>
      </div>
    </div>

    {/* Content side */}
    <div className={`${reverse ? 'lg:[direction:ltr]' : ''}`}>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-semibold uppercase tracking-wide mb-4"
        style={{ backgroundColor: founder.accentBg, color: founder.accentColor }}>
        Co-Founder
      </div>
      <h2 className="font-heading text-[32px] md:text-[38px] font-extrabold text-slate-900 leading-tight tracking-tight">
        {founder.name}
      </h2>
      <p className="text-[15px] font-medium mt-1" style={{ color: founder.accentColor }}>{founder.tagline}</p>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 my-5">
        {founder.stats.map((s) => (
          <div key={s.l} className="rounded-xl p-3 text-center border border-[#c9a24b]/25" style={{ backgroundColor: '#FBF9F4' }}>
            <p className="font-heading font-bold text-[18px] text-slate-900 leading-none">{s.v}</p>
            <p className="text-[11px] text-slate-500 mt-1">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Bio */}
      <div className="space-y-3">
        {founder.bio.map((p, i) => (
          <p key={i} className="text-slate-600 leading-relaxed text-[15px]">{p}</p>
        ))}
      </div>

      {/* Credentials */}
      <ul className="mt-5 space-y-2">
        {founder.credentials.map((c, i) => (
          <li key={i} className="flex items-center gap-3 text-[14px] text-slate-700">
            <div className="w-7 h-7 rounded-md grid place-items-center flex-none" style={{ backgroundColor: founder.accentBg }}>
              <c.icon className="w-3.5 h-3.5" style={{ color: founder.accentColor }} />
            </div>
            {c.text}
          </li>
        ))}
      </ul>

      {/* LinkedIn CTA */}
      <a
        href={founder.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2.5 mt-6 px-5 py-2.5 rounded-lg text-white font-semibold text-[14px] hover:brightness-110 transition shadow-sm"
        style={{ backgroundColor: '#0a66c2' }}
      >
        <Linkedin className="w-4 h-4" />
        Connect on LinkedIn
      </a>
    </div>
  </div>
);

const Team = () => (
  <main>
    <SEO
      title="Meet the Founding Team | Saurabh & Smriti Makharia | Finirio"
      description="Meet Saurabh Makharia (Founder & CEO) and Smriti Makharia (Founder & Director) — the visionary duo behind Finirio Technologies, building India's most trusted financial services platform."
      path="/team"
      keywords="finirio founders, saurabh makharia, smriti makharia, finirio technologies founding team"
    />
    <PageBanner title="Meet the Founding Team" crumbs={['About Us', 'Founding Team']} />

    {/* Intro strip */}
    <div className="navy-panel py-5 relative">
      <div className="hairline-gold absolute top-0 left-0 right-0" />
      <div className="max-w-[900px] mx-auto px-4 text-center">
        <p className="text-white text-[15px] leading-relaxed opacity-90">
          Finirio Technologies was incorporated in <strong>February 2024</strong> with a single mission —
          make financial services transparent, accessible and human. Today, with <strong>₹6,000 Cr+ disbursed</strong> and
          <strong> 10,000+ families served</strong>, that mission is very much alive.
        </p>
      </div>
    </div>

    {/* Founders */}
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8 space-y-20 md:space-y-28">
        {FOUNDERS.map((f, i) => (
          <FounderCard key={f.name} founder={f} reverse={i % 2 !== 0} />
        ))}
      </div>
    </section>

    {/* Shared values */}
    <section className="py-16" style={{ backgroundColor: '#FBF9F4' }}>
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8">
        <div className="text-center mb-10">
          <p className="eyebrow-lux mb-2">What Drives Us</p>
          <h2 className="font-heading text-[30px] md:text-[36px] font-bold text-[#0b2545] tracking-tight">
            Built on shared values
          </h2>
          <span className="block h-[2px] w-16 mt-4 rounded-full mx-auto" style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887)' }} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, color: '#1e73be', bg: '#eaf3fb', t: 'Transparency', d: 'Every rate, fee and process is explained clearly — no hidden surprises, ever.' },
            { icon: Users, color: '#2ea690', bg: '#e7f6f2', t: 'Client First', d: 'We measure success by your outcomes, not the number of transactions we close.' },
            { icon: BadgeCheck, color: '#7c3aed', bg: '#f3f0ff', t: 'Credibility', d: 'AMFI registered, MCA incorporated, SEBI compliant — built to be trusted.' },
            { icon: TrendingUp, color: '#d97706', bg: '#fef9ee', t: 'Growth', d: 'From ₹0 to ₹6,000 Cr+ disbursed — we help our clients and ourselves grow.' },
          ].map((v) => (
            <div key={v.t} className="card-lux p-6 text-center h-full">
              <div className="w-12 h-12 rounded-xl grid place-items-center mx-auto mb-4" style={{ backgroundColor: v.bg }}>
                <v.icon className="w-6 h-6" style={{ color: v.color }} />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-2">{v.t}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-14 bg-white border-t border-slate-100">
      <div className="max-w-[700px] mx-auto px-4 text-center">
        <h2 className="font-heading text-[26px] md:text-[30px] font-bold text-[#0b2545] mb-3">
          Ready to work with us?
        </h2>
        <p className="text-slate-600 mb-7 leading-relaxed">
          Whether you need a home loan, investment advisory, or want to partner with us —
          Saurabh and Smriti's team is here to help.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/apply-now-cbi" className="btn-lux">
            Apply for a Loan <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/investments"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold border border-[#2ea690] text-[#2ea690] hover:bg-[#2ea690]/5 transition">
            Explore Investments
          </Link>
          <Link to="/become-partner"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition">
            Become a Partner
          </Link>
        </div>
      </div>
    </section>
  </main>
);

export default Team;
